import { type FC } from "react";
import { Loader2Icon, CheckCircle2Icon, BookOpenIcon, XCircleIcon } from "lucide-react";
import { Card } from "../ui/card";
import { cn } from "@/lib/utils";

// Helper to format comparator for display
const formatComparator = (comparator: string): string => {
  const normalized = comparator.toUpperCase();
  const map: Record<string, string> = {
    'EQUALS': '=',
    'NOT_EQUALS': '≠',
    'IN': 'is one of',
    'NOT_IN': 'is not one of',
    'CONTAINS': 'contains',
    'NOT_CONTAINS': 'does not contain',
    'GT': '>',
    'GTE': '≥',
    'LT': '<',
    'LTE': '≤',
    'EMPTY': 'is empty',
    'NOT_EMPTY': 'is not empty',
  };
  return map[normalized] || comparator;
};

// Helper to format dimension filters for display
const formatDimensionFilters = (filters: any[]): string => {
  if (!filters || filters.length === 0) {
    return 'all data';
  }
  
  return filters.map((filter: any) => {
    const dimension = filter.dimension || 'Unknown';
    const comparator = formatComparator(filter.comparator || '');
    const value = Array.isArray(filter.value) 
      ? filter.value.join(', ') 
      : (filter.value || '');
    
    if (filter.comparator === 'IN' || filter.comparator === 'NOT_IN') {
      return `${dimension} ${comparator} [${value}]`;
    } else if (filter.comparator === 'EMPTY' || filter.comparator === 'NOT_EMPTY') {
      return `${dimension} ${comparator}`;
    } else {
      return `${dimension} ${comparator} ${value}`;
    }
  }).join(', ');
};

// Custom tool component for create_context_item tool
const ContextToolCallComponent: FC<{
  toolName: string;
  argsText: string;
  result?: unknown;
  status?: { type: "running" | "complete" | "incomplete"; reason?: string };
  args?: any;
  toolCallId?: string;
  [key: string]: any; // Accept any additional props
}> = ({ toolName, argsText, result, status, args, toolCallId }) => {
  const isRunning = status?.type === "running" || (status === undefined && result === undefined);
  const isError = status?.type === "incomplete" && status.reason === "error";
  // Tool is complete only when status is explicitly 'complete' from backend
  const isComplete = status?.type === "complete" && !isError;

  // Parse args to get context details
  let contextName: string = "";
  let contextDescription: string = "";
  let dimensionFilters: any[] = [];
  let hasValidArgs = false;
  
  try {
    const parsedArgs = typeof argsText === 'string' ? JSON.parse(argsText) : (args || argsText);
    contextName = parsedArgs?.name || "";
    contextDescription = parsedArgs?.description || "";
    dimensionFilters = parsedArgs?.dimension_filters || [];
    hasValidArgs = !!contextName;
  } catch (e) {
    // If parsing fails, try to extract from argsText string
    if (typeof argsText === 'string') {
      const nameMatch = argsText.match(/"name"\s*:\s*"([^"]+)"/);
      if (nameMatch) {
        contextName = nameMatch[1];
        hasValidArgs = true;
      }
    }
  }

  // Parse result to check for success/error
  let successMessage: string = "";
  let errorMessage: string = "";
  let isSuccess = false;
  
  if (result !== undefined) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      if (parsed.success) {
        isSuccess = true;
        successMessage = parsed.message || `Context "${contextName || '...'}" has been saved successfully.`;
      } else {
        errorMessage = parsed.message || parsed.error || "Failed to save context.";
      }
    } catch (e) {
      // If parsing fails, check if result is a plain string
      if (typeof result === 'string') {
        if (result.toLowerCase().includes('error') || result.toLowerCase().includes('failed')) {
          errorMessage = result;
        } else if (result.toLowerCase().includes('success')) {
          isSuccess = true;
          successMessage = result;
        }
      }
    }
  }

  // Detect error from status or result
  const hasError = isError || !!errorMessage;

  // Format the plain-english representation
  const filtersText = formatDimensionFilters(dimensionFilters);
  const contextSummary = contextName 
    ? `${contextName}${contextDescription ? `: ${contextDescription}` : ''} (applies to ${filtersText})`
    : 'Adding context...';

  return (
    <Card>
      <div className={cn(
        "p-3",
        isRunning ? "bg-primary/10" : hasError ? "bg-destructive/10" : "bg-muted/50"
      )}>
        <div className="flex items-center gap-2">
          {isRunning ? (
            <div className="flex items-center gap-2">
              <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm m-0 text-foreground font-medium">
                  Adding context...
                </p>
                {hasValidArgs && (
                  <p className="text-xs m-0 mt-1 text-muted-foreground">
                    {contextSummary}
                  </p>
                )}
              </div>
            </div>
          ) : hasError ? (
            <div className="flex items-center gap-2">
              <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm m-0 text-destructive font-medium">
                  Failed to save context
                </p>
                {errorMessage && (
                  <p className="text-xs m-0 mt-1 text-destructive/80">
                    {errorMessage}
                  </p>
                )}
              </div>
            </div>
          ) : isComplete && isSuccess ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <BookOpenIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <p className="text-sm m-0 text-muted-foreground">
                  {hasValidArgs ? `Adding context: ${contextSummary}` : 'Adding context...'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">
                  {successMessage || `Context "${contextName || '...'}" has been saved successfully.`}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
              <p className="text-sm m-0 text-foreground">
                Adding context...
              </p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

// Type assertion to match assistant-ui's expected type
export const ContextToolCall = ContextToolCallComponent as React.ComponentType<any>;

